import { useState, FormEvent, useEffect } from "react";
import { useSharedState } from "state";
import ModalComponent from "components/modal";
import Select, { components, GroupProps } from "react-select";
import {
  deleteBudgetReportHistory,
  downloadBudgetReport,
  generateBudgetReport,
  getBudgetReportHistory,
} from "api/budget_request";
import moment from "moment";
import { BudgetReport, ExcelPayload } from "types/report";
import { BudgetRequestHistory, BudgetRequestHistoryResponse } from "types/budget";
import { IconDownload, IconEye, IconTrash } from "@tabler/icons-react";
import DangerModal from "components/modal/danger";

type FormErrors = Record<string, boolean>;

type FormRecords = {
  company_id: number;
  department_id: number;
  report_template_index: number;
  year: number;
};

type SelectOption = {
  value: number;
  label: string;
};

const REQUIRED_FIELDS = ["company_id", "department_id", "report_template_index", "year"] as const;

const getInputErrorMessage = (fieldId: string) =>
  fieldId === "file" ? "Добавьте договор" : "Введите корректное значение";

export default function NewReport() {
  const {
    departments,
    getDepartmentsData,
    companies,
    getCompaniesData,
    notify,
    setReport,
    setReportDetails,
    reportTemplates,
    setReportType,
    setExcelName,
    getReportTemplatesData,
    isAdmin,
  } = useSharedState();

  const [formRecords, setFormRecords] = useState<FormRecords>({
    company_id: 0,
    department_id: 0,
    report_template_index: -1,
    year: moment().year(),
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [reportToDelete, setReportToDelete] = useState<BudgetRequestHistory | undefined>(undefined);

  const [reportHistory, setReportHistory] = useState<BudgetRequestHistoryResponse | undefined>(undefined);

  const [isFormLoading, setFormLoading] = useState<boolean>(false);
  const [isFormSubmitted, setFormSubmitted] = useState<boolean>(false);

  const deleteReportDanger = `Вы действительно хотите удалить отчёт "${reportToDelete?.budget_report_template_name}" из истории?`;

  useEffect(() => {
    getDepartmentsData();
    getCompaniesData();
    getReportTemplatesData();
    getBudgetReportHistoryData();
  }, []);

  const openReportModal = () => {
    const button = document.createElement("button");
    button.setAttribute("data-bs-toggle", "modal");
    button.setAttribute("data-bs-target", "#modal");
    document.body.appendChild(button);
    button.click();
    button.remove();
  };

  const budgetFields = [
    {
      id: "company_id",
      title: "Юр. лицо",
      type: "select",
      placeholder: "Выберите юр. лицо",
      options: companies
        .filter((com) => com.status)
        .map((com) => ({
          label: com.company,
          value: com.company_id,
        })),
      isMulti: false,
    },
    {
      id: "department_id",
      title: "Отдел",
      type: "select",
      placeholder: "Выберите отдел",
      options: isAdmin
        ? [
            {
              label: "Все отделы",
              options: departments
                .filter((dep) => dep.status)
                .map((dep) => ({
                  label: dep.department,
                  value: dep.department_id,
                })),
            },
          ]
        : departments
            .filter((dep) => dep.status)
            .map((dep) => ({
              label: dep.department,
              value: dep.department_id,
            })),
      isMulti: isAdmin,
    },
    {
      id: "report_template_index",
      title: "Шаблон отчёта",
      type: "select",
      placeholder: "Выберите шаблон",
      options: reportTemplates
        .filter((rep) => rep.status)
        .map((rep, index) => ({
          label: rep.template_name,
          value: index + 1,
        })),
      isMulti: false,
    },
    {
      id: "year",
      title: "Год",
      type: "number",
      placeholder: undefined,
      options: undefined,
      isMulti: undefined,
    },
  ] as const;

  const validateForm = () => {
    const newErrors: FormErrors = {};
    REQUIRED_FIELDS.forEach((field) => {
      const value = formRecords[field];
      if (Array.isArray(value) ? value.length === 0 : !value || !value?.toString().trim()) {
        newErrors[field] = true;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  type InputChangeEvent = React.ChangeEvent<HTMLInputElement>;
  type SelectChangeEvent = readonly SelectOption[] | null;
  type SingleSelectEvent = SelectOption | null;

  const handleInputChange = (e: InputChangeEvent | SelectChangeEvent | SingleSelectEvent, fieldId?: string) => {
    if (e && "target" in e) {
      // Handle input change
      const { name, value } = e.target;
      setFormRecords((prev) => ({ ...prev, [name]: name === "year" ? value.slice(0, 4) : value }));
      setErrors((prev) => ({ ...prev, [name]: false }));
    } else {
      // Handle select change
      // @ts-ignore
      const values = Array.isArray(e) ? e.map((option) => option.value) : e ? e.value : "";
      setFormRecords((prev) => ({ ...prev, [fieldId!]: values }));
      setErrors((prev) => ({ ...prev, [fieldId!]: false }));
    }
  };

  const getBudgetReportHistoryData = async () => {
    try {
      let res: BudgetRequestHistoryResponse = await getBudgetReportHistory();
      setReportHistory(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении истории");
      setFormLoading(false);
    }
  };

  const applyReportFromHistory = async (reportHistoryId: number, action: "download" | "open") => {
    const report = reportHistory?.items.find((r) => r.id === reportHistoryId);

    switch (action) {
      case "download":
        const payload: ExcelPayload = {
          file_name: report?.response.file_name,
          file_path: report?.response.file_path,
        };

        await onExcelDownload(payload);
        break;

      case "open":
        setReport(report?.response as any);
        setReportDetails(report?.payload as any);
        setReportType(report?.payload.structure);
        setExcelName(report?.budget_report_template_name || "");
        notify("Отчёт сформирован", "success");

        setFormSubmitted(true);
        break;

      default:
        break;
    }
  };

  const handleReportDelete = async () => {
    openReportModal();
    setFormLoading(true);

    try {
      await deleteBudgetReportHistory(Number(reportToDelete?.id));
      notify("Отчёт удален из истории", "success");

      setReportToDelete(undefined);
      getBudgetReportHistoryData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при удалении отчёта из истории");
    } finally {
      setFormLoading(false);
    }
  };

  const onExcelDownload = async (payload: ExcelPayload) => {
    try {
      setFormLoading(true);
      const res: any = await downloadBudgetReport(payload);

      const localName = reportTemplates[formRecords.report_template_index - 1]?.template_name || payload.file_name;
      const fileName = `${localName} ${moment().format("DD.MM.YYYY HH:mm")}.xlsx`;
      const blob = new Blob([res], { type: "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName.replaceAll(" ", "_");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      notify("Скачивание завершено", "success");
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении файла");
    } finally {
      setFormLoading(false);
    }
  };

  const generateReport = async () => {
    setFormLoading(true);
    try {
      const description = reportTemplates[formRecords.report_template_index - 1].json_description;
      const payload = {
        budget_report_template_id: reportTemplates[formRecords.report_template_index - 1].budget_report_template_id,
        year: formRecords.year,
        structure: description.structure,
        add_forecast: description.add_forecast,
        groupings: description.groupings,
        company_id: [formRecords.company_id],
        department_id: Array.isArray(formRecords.department_id)
          ? formRecords.department_id
          : formRecords.department_id === 9999
          ? undefined
          : formRecords.department_id === 0
          ? undefined
          : [formRecords.department_id],
        need_columns: description.add_forecast
          ? [...description.need_columns].concat("forecast_value")
          : description.need_columns,
        additional_formulas: description.additional_formulas,
        need_formulas: description.need_formulas,
        generate_excel: true,
      };
      const res: BudgetReport = await generateBudgetReport(payload);

      return { res, payload };
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при формировании отчёта");
      setFormLoading(false);
    }
  };

  const downloadExcel = async () => {
    if (!validateForm()) {
      notify("Заполните все поля", "danger");
      return;
    }

    setFormLoading(true);
    try {
      const localResponse = await generateReport();
      const payload: ExcelPayload = {
        file_name: localResponse?.res.file_name!,
        file_path: localResponse?.res.file_path!,
      };

      await onExcelDownload(payload);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении файла");
    } finally {
      setFormLoading(false);
    }
  };

  const submitBudget = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      notify("Заполните все поля", "danger");
      return;
    }

    try {
      const localResponse = await generateReport();

      setReport(localResponse?.res!);
      setReportDetails(localResponse?.payload!);
      setReportType(reportTemplates[formRecords.report_template_index - 1].json_description.structure);
      setExcelName(reportTemplates[formRecords.report_template_index - 1].template_name);
      notify("Отчёт сформирован", "success");

      setFormSubmitted(true);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при формировании отчёта");
    } finally {
      setFormLoading(false);
    }
  };

  const addAllDepartments = () => {
    const allDepartmentIds: any = departments.filter((dep) => dep.status).map((dep) => dep.department_id);

    setFormRecords((prev) => ({
      ...prev,
      department_id: allDepartmentIds,
    }));
    setErrors((prev) => ({ ...prev, department_id: false }));
  };

  // Helper to flatten options
  const flattenOptions = (opts: any[] = []) => opts.flatMap((opt) => (opt.options ? opt.options : opt));

  const groupStyles = {
    border: `2px dotted green`,
    borderRadius: "5px",
    background: "#f2fcff",
  };

  const Group = (props: GroupProps<SelectOption, boolean>) => (
    <div style={groupStyles}>
      <components.Group {...props} />
    </div>
  );

  const renderField = ({ id, title, type, placeholder, options, isMulti }: (typeof budgetFields)[number]) => {
    return (
      <div className={"col-lg-6"} key={id}>
        <div className="mb-3">
          <label className="form-label required">{title}</label>
          <div className="input-icon mb-3">
            {type === "number" ? (
              <input
                type="number"
                className={`form-control ${errors[id] ? "is-invalid" : ""}`}
                name={id}
                value={formRecords[id]}
                onChange={handleInputChange}
                disabled={isFormLoading}
              />
            ) : (
              <Select<SelectOption, boolean>
                classNamePrefix="react-select"
                name={id}
                placeholder={placeholder}
                value={
                  isMulti
                    ? flattenOptions(options)?.filter((opt: any) =>
                        Array.isArray(formRecords[id])
                          ? // @ts-ignore
                            formRecords[id].includes(opt.value)
                          : formRecords[id] === opt.value
                      )
                    : flattenOptions(options)?.find((opt: any) => opt.value === formRecords[id]) ?? null
                }
                onChange={(newValue) => handleInputChange(newValue, id)}
                isDisabled={isFormLoading}
                options={options}
                components={{
                  Group,
                  GroupHeading: (props) => (
                    <div className="d-flex align-items-center justify-content-between">
                      <components.GroupHeading {...props} />
                      <div className="select-group-header-btn" onClick={addAllDepartments}>
                        Добавить все
                      </div>
                    </div>
                  ),
                }}
                isSearchable
                isMulti={isMulti}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            )}

            <div className="invalid-feedback">{getInputErrorMessage(id)}</div>
            {isFormLoading && (
              <span className="input-icon-addon">
                <div className="spinner-border spinner-border-sm text-secondary" role="status" />
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const newBudgetBody = (
    <>
      <form id="budgetForm" className="row">
        {budgetFields.map(renderField)}
      </form>

      <div className="row row-cards">
        <div className="col-12">
          <div className="card rounded-2xl">
            <div className="card-header">
              <h3 className="card-title">Последние отчёты</h3>
            </div>
            <div className="card-body overflow-y-auto" style={{ maxHeight: 300 }}>
              <div className="row g-3">
                {reportHistory?.items.length ? (
                  reportHistory.items.map((reportItem) => (
                    <div className="col-lg-12">
                      <div className="row g-3 align-items-center">
                        <div className="col text-truncate">
                          <div className="d-flex align-items-center justify-content-between">
                            <div>
                              <div className="text-reset d-block text-truncate">
                                {reportItem.budget_report_template_name || "-"}
                              </div>
                              <div className="text-secondary text-truncate mt-n1">
                                {moment(reportItem.updated_at).format("DD.MM.YYYY HH:mm") || "-"}
                              </div>
                            </div>
                            <div className="d-flex gap-2">
                              <button
                                type="button"
                                title="Скачать Excel"
                                className="btn btn-icon btn-primary d-flex gap-1"
                                onClick={() => applyReportFromHistory(reportItem.id, "download")}
                              >
                                {isFormLoading ? (
                                  <div className="spinner-border spinner-border-sm" role="status" />
                                ) : (
                                  <IconDownload className="icon" />
                                )}
                              </button>
                              <button
                                type="button"
                                title="Показать отчёт"
                                className="btn btn-icon btn-primary d-flex gap-1"
                                onClick={() => applyReportFromHistory(reportItem.id, "open")}
                              >
                                {isFormLoading ? (
                                  <div className="spinner-border spinner-border-sm" role="status" />
                                ) : (
                                  <IconEye className="icon" />
                                )}
                              </button>
                              <button
                                type="button"
                                title="Удалить"
                                className="btn btn-icon btn-danger d-flex gap-1"
                                data-bs-toggle="modal"
                                data-bs-target="#modal-delete-history"
                                onClick={() => setReportToDelete(reportItem)}
                              >
                                {isFormLoading ? (
                                  <div className="spinner-border spinner-border-sm" role="status" />
                                ) : (
                                  <IconTrash className="icon" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-6">
                    <div className="row g-3 align-items-center">
                      <div className="col text-truncate">
                        <div className="text-secondary text-truncate mt-n1">Нет данных</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const newBudgetFooter = (
    <>
      <button type="button" className="btn btn-primary d-flex gap-1" disabled={isFormLoading} onClick={downloadExcel}>
        Скачать Excel
        {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
      </button>
      <button type="button" className="btn btn-primary d-flex gap-1" disabled={isFormLoading} onClick={submitBudget}>
        Показать отчёт
        {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
      </button>
    </>
  );

  return (
    <>
      <ModalComponent
        header={"Новый отчёт"}
        body={newBudgetBody}
        footer={newBudgetFooter}
        disableClose={isFormLoading}
        shouldModalClose={isFormSubmitted}
      />
      <DangerModal
        modal_id="modal-delete-history"
        dangerDescription={deleteReportDanger}
        cancel={openReportModal}
        confirm={handleReportDelete}
      />
    </>
  );
}
